import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import { getMatch } from "../../components/utils/MatchUtils";
import { useNavigate } from 'react-router-dom';

const MatchJoin: React.FC = () => {
  const navigate = useNavigate();

  const [matchId, setMatchId] = useState<string>("");
  const [error, setError] = useState<string>("");
  const inputRefs = useRef<HTMLInputElement[]>([]); // Manage refs for input fields

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""); // Only allow alphanumeric
    if (value.length > 1) return;

    const splitMatchId = matchId.split("");
    splitMatchId[index] = value || ""; // Update the specific character
    const updatedMatchId = splitMatchId.join("");

    setMatchId(updatedMatchId);

    // Automatically move to the next field
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !matchId[index] && index > 0) {
      // Move to the previous field if current is empty and Backspace is pressed
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleJoinMatch = () => {
    const theMatchId = `${matchId.slice(0, 3)}-${matchId.slice(3)}`;
    
    getMatch(theMatchId).then((match)=>{
      if(match!==null){
        navigate(`/matches/${theMatchId}`);
      }
    }).catch((reason)=>{
      setError(reason.message)
    })

  };

  return (
    <Container maxWidth="md" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h4" gutterBottom>
          Join a Match
        </Typography>
        {error === "" && <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          Enter your match ID below
        </Typography>}
        {error !== "" && <Typography variant="subtitle1" color="error" gutterBottom>
          {error}
        </Typography>}

        <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
          <Grid container spacing={2} justifyContent="center">
            {/* First 3 Inputs */}
            {[0, 1, 2].map((index) => (
              <Grid item key={index} xs={2} sm={1}>
                <TextField
                  inputRef={(el) => (inputRefs.current[index] = el!)} // Store ref for each input
                  variant="outlined"
                  inputProps={{
                    maxLength: 1, // Limit each box to one character
                    style: { textAlign: "center", fontSize: "2rem" },
                  }}
                  value={matchId[index] || ""}
                  onChange={(e) => handleInputChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                />
              </Grid>
            ))}

            {/* Dash */}
            <Grid item key="dash" xs={1}>
              <Typography
                variant="h1"
                sx={{
                  lineHeight: "3.5rem",
                  mx: 1, // Margin between groups
                }}
              >
                -
              </Typography>
            </Grid>

            {/* Last 3 Inputs */}
            {[3, 4, 5].map((index) => (
              <Grid item key={index}  xs={2} sm={1}>
                <TextField
                  inputRef={(el) => (inputRefs.current[index] = el!)} // Store ref for each input
                  variant="outlined"
                  inputProps={{
                    maxLength: 1, // Limit each box to one character
                    style: { textAlign: "center", fontSize: "2rem" },
                  }}
                  value={matchId[index] || ""}
                  onChange={(e) => handleInputChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                />
              </Grid>
            ))}
          </Grid>

          {/* Join Button */}
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 4 }}
            onClick={handleJoinMatch}
            disabled={matchId.length !== 6}
          >
            Join
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default MatchJoin;
